/**
 * 튜토리얼 포인터(손가락) 연출 — Phaser 뷰. 한 영역 위에서 탭하거나, 한 영역→다른 영역으로 드래그하는
 * 손가락 아이콘을 반복 재생한다(예: "보유 모듈을 그리드로 끌어다 놓으세요").
 *
 * 손가락 텍스처는 앱이 주입한다(`pointerTextureKey`) — 패키지가 특정 게임 에셋을 모르게 하기 위함(DIP).
 * 오버레이 Container와 분리된 독립 GameObject(씬 직속 + depth)라, 오버레이의 dim/하이라이트 재그리기
 * (`removeAll`)와 충돌 없이 `clear()`로 스스로 정리한다.
 */
import type Phaser from "phaser";
import type { TutorialRect, TutorialGesture } from "./types.js";

function centerOf(r: TutorialRect): { x: number; y: number } {
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

export class TutorialPointer {
  private image: Phaser.GameObjects.Image | null = null;
  private readonly tweens: Phaser.Tweens.Tween[] = [];

  constructor(
    private readonly scene: Phaser.Scene,
    /** 손가락 렌더 깊이 — 하이라이트/메시지 위에 보이도록 오버레이 depth보다 살짝 위. */
    private readonly depth: number,
  ) {}

  /**
   * 연출 갱신 — 이전 손가락/트윈을 정리하고 새로 그린다.
   * @param gesture none = 연출 없음, tap = from 위 제자리 탭, drag = from→to 이동.
   * @param from    출발/대상 Rect(없으면 연출 없음).
   * @param to      도착 Rect(drag일 때만 사용).
   * @param textureKey 손가락 텍스처 키(미주입/미로드 시 연출 없음).
   */
  show(
    gesture: TutorialGesture,
    from: TutorialRect | null,
    to: TutorialRect | null,
    textureKey: string | undefined,
  ): void {
    this.clear();
    if (gesture === "none" || !from || !textureKey) return;
    if (!this.scene.textures.exists(textureKey)) return; // 에셋 미로드 안전 가드

    const start = centerOf(from);
    const img = this.scene.add
      .image(start.x, start.y, textureKey)
      .setDepth(this.depth)
      .setAlpha(0.95);
    this.image = img;

    // 상시 탭 펄스(누르는 느낌) — tap·drag 공통.
    this.tweens.push(
      this.scene.tweens.add({
        targets: img,
        scaleX: 0.82,
        scaleY: 0.82,
        duration: 260,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      }),
    );

    // drag — from 중심 → to 중심 이동 반복(도착 hold 후 출발점으로 리셋).
    if (gesture === "drag" && to) {
      const end = centerOf(to);
      this.tweens.push(
        this.scene.tweens.add({
          targets: img,
          x: end.x,
          y: end.y,
          duration: 850,
          ease: "Sine.easeInOut",
          hold: 300,
          repeat: -1,
          repeatDelay: 450,
        }),
      );
    }
  }

  /** 손가락/트윈 제거. */
  clear(): void {
    for (const tw of this.tweens) tw.remove();
    this.tweens.length = 0;
    this.image?.destroy();
    this.image = null;
  }
}
